import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface ToolCard {
  icon: string;
  title: string;
  description: string;
  image: string;
  caseStudy: {
    customerName: string;
    industry: string;
    painPoint: string;
    situation: string;
    solution: string;
    results: string[];
  };
}

const tools: ToolCard[] = [
  {
    icon: 'ri-stack-line',
    title: 'Smart Inventory Tracker',
    description: 'Automatically track product quantities, incoming shipments, and outgoing sales.',
    image: 'https://public.readdy.ai/ai/img_res/47d080851af48fe9914e99622d807d50.jpg',
    caseStudy: {
      customerName: 'Mumbai Electronics Distributor',
      industry: 'Electronics Wholesale',
      painPoint: 'Lost ₹2.5L monthly due to stockouts and overstocking',
      situation: 'The owner was managing 450+ SKUs across 3 warehouses using Excel sheets. Stock counts were done manually every week, leading to frequent discrepancies. They would often run out of fast-moving items while overstocking slow movers.',
      solution: 'We built a mobile-first inventory system that syncs across all warehouses in real-time. Barcode scanning for quick updates, automatic low-stock alerts via WhatsApp, and AI-powered reorder suggestions based on sales velocity.',
      results: [
        'Reduced stockouts by 87%',
        'Cut excess inventory by 42%',
        'Saved 15 hours/week on manual counting',
        'ROI achieved in 6 weeks'
      ]
    }
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Attendance & Scheduling',
    description: 'Digital attendance tracking with automated scheduling and shift management.',
    image: 'https://readdy.ai/api/search-image?query=digital%20employee%20attendance%20dashboard%20showing%20team%20schedule%20calendar%20with%20check-in%20status%20indicators%20modern%20office%20workspace%20professional%20time%20tracking%20interface%20clean%20organized%20layout&width=600&height=400&seq=ai-tool-attendance-002&orientation=landscape',
    caseStudy: {
      customerName: 'Bangalore Security Services',
      industry: 'Security & Facility Management',
      painPoint: 'Managing 200+ guards across 40 sites with paper registers',
      situation: 'Supervisors spent 3-4 hours daily collecting attendance from physical registers at different sites. Payroll processing took 2 full days every month. No way to verify if guards actually showed up on time or left early.',
      solution: "Built a GPS-enabled mobile app where guards check in/out with photo verification. Supervisors get real-time dashboards showing who's present at each site. Automated shift scheduling with conflict detection and instant notifications for no-shows.",
      results: [
        'Real-time attendance visibility',
        'Payroll processing reduced to 2 hours',
        'Eliminated buddy punching completely',
        'Client satisfaction increased 35%'
      ]
    }
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Payment Tracker',
    description: 'Clear visibility of who owes you, payment history, and automated reminders.',
    image: 'https://readdy.ai/api/search-image?query=financial%20payment%20tracking%20dashboard%20showing%20invoice%20status%20payment%20due%20dates%20and%20customer%20accounts%20receivable%20organized%20clean%20interface%20professional%20accounting%20visualization%20modern%20fintech%20design&width=600&height=400&seq=ai-tool-payment-003&orientation=landscape',
    caseStudy: {
      customerName: 'Delhi Construction Supplier',
      industry: 'Building Materials Supply',
      painPoint: '₹45L stuck in unpaid invoices, no clear tracking system',
      situation: 'The owner had 60+ contractors buying on credit. Payment tracking was done in a notebook and WhatsApp messages. He had no idea who owed what or for how long. Chasing payments meant scrolling through months of chat history.',
      solution: 'Created a simple payment tracking system that logs every sale, tracks due dates, and sends automated WhatsApp reminders 3 days before and on the due date. Dashboard shows aging analysis and payment history for each customer.',
      results: [
        'Collected ₹28L in overdue payments within 60 days',
        'Average payment delay reduced from 45 to 18 days',
        'Zero time spent chasing payments manually',
        'Improved cash flow by 156%'
      ]
    }
  }
];

function ToolCard({ tool, index, onClick }: { tool: ToolCard; index: number; onClick: () => void }) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`transition-all duration-700 cursor-pointer ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group h-[500px]">
        <div className="relative h-56 overflow-hidden">
          <img
            src={tool.image}
            alt={tool.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center">
              <i className={`${tool.icon} text-2xl text-teal-600`}></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">{tool.title}</h3>
          </div>
          <p className="text-slate-600 leading-relaxed">{tool.description}</p>
          <div className="mt-6 text-teal-600 font-semibold flex items-center gap-2">
            <span>Click to see real case study</span>
            <i className="ri-arrow-right-line"></i>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExamplesSection() {
  const navigate = useNavigate();
  const [selectedTool, setSelectedTool] = useState<ToolCard | null>(null);
  const { ref: titleRef, inView: titleInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const handleTellUsClick = () => {
    navigate('/request-quote', { state: { mode: 'ai-tool' } });
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-300/30 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Real Tools We've Built for Real Businesses
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            These aren't mockups or concepts. These are actual custom tools we built for small and medium businesses across India. Click on each card to see the full story.
          </p>
        </div>

        {/* Tool Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tools.map((tool, index) => (
            <ToolCard 
              key={index} 
              tool={tool} 
              index={index}
              onClick={() => setSelectedTool(tool)}
            />
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-16">
          <button
            onClick={handleTellUsClick}
            className="bg-gradient-to-r from-teal-600 to-slate-700 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:from-teal-700 hover:to-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap inline-flex items-center gap-3 cursor-pointer"
          >
            <i className="ri-chat-3-fill text-xl"></i>
            Tell Us What You Need
          </button>
        </div>
      </div>

      {/* Case Study Modal - Click to open */}
      {selectedTool && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-8 pt-24"
          onClick={() => setSelectedTool(null)}
        >
          <div 
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedTool(null)}
              className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>

            <div className="p-10 text-white">
              {/* Customer Header */}
              <div className="mb-8 pb-6 border-b border-white/20">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-16 h-16 bg-teal-500 rounded-xl flex items-center justify-center">
                    <i className={`${selectedTool.icon} text-3xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="text-3xl font-bold">{selectedTool.caseStudy.customerName}</h4>
                    <p className="text-lg text-slate-300">{selectedTool.caseStudy.industry}</p>
                  </div>
                </div>
              </div>

              {/* Pain Point */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <i className="ri-error-warning-line text-2xl text-red-400"></i>
                  <h5 className="font-bold text-xl uppercase tracking-wide text-red-400">The Problem</h5>
                </div>
                <p className="text-white/90 text-lg leading-relaxed">{selectedTool.caseStudy.painPoint}</p>
              </div>

              {/* Situation */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <i className="ri-file-list-line text-2xl text-slate-300"></i>
                  <h5 className="font-bold text-xl uppercase tracking-wide text-slate-300">What They Were Facing</h5>
                </div>
                <p className="text-white/80 text-lg leading-relaxed">{selectedTool.caseStudy.situation}</p>
              </div>

              {/* Solution */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <i className="ri-lightbulb-line text-2xl text-teal-400"></i>
                  <h5 className="font-bold text-xl uppercase tracking-wide text-teal-400">What We Built</h5>
                </div>
                <p className="text-white/90 text-lg leading-relaxed">{selectedTool.caseStudy.solution}</p>
              </div>

              {/* Results */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <i className="ri-line-chart-line text-2xl text-green-400"></i>
                  <h5 className="font-bold text-xl uppercase tracking-wide text-green-400">Results</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTool.caseStudy.results.map((result, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-white/90 text-base font-medium">{result}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}